import { APP_INITIALIZER, Injector, NgModule, Optional, SkipSelf } from '@angular/core';
import { NgForm } from '@angular/forms';

import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';

import { GOOGLE_MAP_KEY } from '@firestitch/address';
import {
  FS_API_REQUEST_INTERCEPTOR,
  FsApi,
  FsApiModule,
} from '@firestitch/api';
import { FS_APP_ACL_CONFIG, FsAppAclModule } from '@firestitch/app-acl';
import { FS_APP_MESSAGE_CONFIG } from '@firestitch/app-message';
import { FsAutocompleteChipsModule } from '@firestitch/autocomplete-chips';
import { BuildConfig, FS_BUILD_CONFIG, FsBuildModule, UpdateAction } from '@firestitch/build';
import { FsCookie } from '@firestitch/cookie';
import { FsDatePickerModule } from '@firestitch/datepicker';
import { FsDialogModule } from '@firestitch/dialog';
import { DRAWER_DEFAULT_CONFIG } from '@firestitch/drawer';
import { FsFileModule } from '@firestitch/file';
import { ButtonStyle, FsFilterModule } from '@firestitch/filter';
import { FsFormModule } from '@firestitch/form';
import { FsBodyModule } from '@firestitch/html';
import { FsHtmlEditorModule } from '@firestitch/html-editor';
import { FsListModule } from '@firestitch/list';
import { FS_MAP_GOOGLE_MAP_KEY } from '@firestitch/map';
import { FsMessage, FsMessageModule } from '@firestitch/message';
import { FsNavModule } from '@firestitch/nav';
import { FsPasswordModule } from '@firestitch/password';
import { FsPhoneModule } from '@firestitch/phone';
import { FsProgressModule } from '@firestitch/progress';
import { FsPromptModule } from '@firestitch/prompt';
import { FsScrollModule } from '@firestitch/scroll';
import { FsScrollbarModule } from '@firestitch/scrollbar';
import { FsSelectionModule } from '@firestitch/selection';
import { FsSigninModule, SigninConfig } from '@firestitch/signin';
import { FsSigninSecurityModule } from '@firestitch/signin-security';
import { FsStore, FsStoreModule } from '@firestitch/store';
import { FsTabsModule } from '@firestitch/tabs';
import { FsTextEditorModule } from '@firestitch/text-editor';
import { FS_TRANSFER_HANDLER, FsTransferService } from '@firestitch/transfer';
import { FsUploadModule } from '@firestitch/upload';

import { of } from 'rxjs';

import { environment } from '@env/environment';

import { AclRoleLevels } from '@app/common';
import {
  AccountData,
  AclRoleData, MessageData, MessageQueueData,
  MessageTemplateData, MessageWebhookData, SessionService,
} from '@app/core';

import { ListDefaultConfig } from './consts';
import { TransferHandler } from './handlers';
import { throwIfAlreadyLoaded } from './helpers';
import { appMessageConfig } from './helpers/app-message-config';
import { ENVIRONMENT } from './injectors';
import { ApiInterceptorFactory } from './interceptors';
import { AppService, SigninService } from './services';


@NgModule({
  imports: [
    FsApiModule.forRoot({ maxFileConnections: 5 }),
    FsPasswordModule.forRoot(),
    FsMessageModule.forRoot(),
    FsTextEditorModule.forRoot(),
    FsSelectionModule,
    FsTabsModule.forRoot(),
    FsNavModule.forRoot({ watchBrowserBackButton: false }),
    FsListModule.forRoot(ListDefaultConfig),
    FsPromptModule.forRoot(),
    FsFormModule.forRoot(),
    FsScrollModule.forRoot(),
    FsFilterModule.forRoot({ 
      case: 'camel', 
      queryParam: true, 
      chips: true, 
      buttonStyle: ButtonStyle.Flat,
    }),
    FsUploadModule.forRoot(),
    FsFileModule.forRoot(),
    FsTabsModule.forRoot(),
    FsAutocompleteChipsModule.forRoot(),
    FsProgressModule.forRoot({ barColor: '#F3726C' }),
    FsScrollbarModule.forRoot(),
    FsSigninSecurityModule.forRoot({ case: 'camel' }),
    FsDatePickerModule.forRoot(),
    FsPhoneModule.forRoot(),
    FsDialogModule.forRoot(),
    FsBodyModule.forRoot(),
    FsMessageModule.forRoot(),
    FsHtmlEditorModule.forRoot({
      activationKey: '2J1B10dD7F6F5A3F3I3cWHNGGDTCWHId1Eb1Oc1Yh1b2Ld1POkE3D3F3C9A4E5A3G3B2G2==',
    }),
    FsSigninModule.forRoot({
      factory: (appService: AppService, signinService: SigninService): SigninConfig => {
        return {
          trustedDeviceExpiryDays: appService.settings.trustedDeviceExpiryDays,
          processSignin: (response, redirect) => signinService
            .processSignin(response, redirect),
          signinMeta: () => of({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        };
      },
      deps: [AppService, SigninService],
    }),
    FsStoreModule,
    FsAppAclModule,
  ],
  providers: [
    NgForm,
    {
      provide: APP_INITIALIZER,
      useFactory: (appService: AppService) => {
        return () => appService.load();
      },
      multi: true,
      deps: [AppService],
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { maxWidth: '95vw', hasBackdrop: true, autoFocus: true },
    },
    {
      provide: FS_TRANSFER_HANDLER,
      useClass: TransferHandler,
      deps: [FsMessage, FsCookie],
    },
    {
      provide: FS_API_REQUEST_INTERCEPTOR,
      useFactory: ApiInterceptorFactory,
      deps: [Injector],
    },
    {
      provide: GOOGLE_MAP_KEY,
      useFactory: (appService: AppService) => {
        return appService.settings.googleMapKey;
      },
      deps: [AppService],
    },
    {
      provide: FS_BUILD_CONFIG,
      useFactory: (): BuildConfig => {
        return {
          updateAction: environment.production ?
            UpdateAction.NavigationUpdate : UpdateAction.PromptUpdate,
        };
      },
    },
    {
      provide: DRAWER_DEFAULT_CONFIG,
      useValue: {
        width: {
          side: {
            min: 300,
          },
        },
      },
    },
    {
      provide: FS_APP_ACL_CONFIG,
      useFactory: (aclRoleData: AclRoleData) => {
        return {
          permissions: aclRoleData.permissions(),
          levels: of(AclRoleLevels),
        };
      },
      deps: [AclRoleData],
    },
    {
      provide: FS_APP_MESSAGE_CONFIG,
      useFactory: appMessageConfig,
      deps: [
        MessageData, MessageQueueData, MessageTemplateData,
        MessageWebhookData, AccountData, SessionService,
      ],
    },
    {
      provide: FS_MAP_GOOGLE_MAP_KEY,
      useFactory: (appService: AppService) => appService.settings.googleMapKey,
      deps: [AppService],
    },    
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        stretchTabs: false,
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', floatLabel: 'auto' },
    },
    FsTransferService,
    FsApi,
    FsStore,
  ],
  exports: [
    FsProgressModule,
    FsBuildModule,
  ],
})
export class CoreModule {

  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }

}
